import {mapToCanActivate, Route} from "@angular/router";
import {TechAppLoginGuard} from "@tech/guards/tech-app-login.guard";
import {sharedKnowledgeArticleDetailResolver} from '@shared/components/knowledge/resolvers/shared-knowledge-article-detail.resolver';
import {sharedKnowledgePathDetailResolver} from '@shared/components/knowledge/resolvers/shared-knowledge-path-detail.resolver';
import {techMenuResolver} from "@tech/resolvers/tech-menu.resolver";
import {techReportPageCanLeaveGuard} from "@tech/guards/tech-report-page-can-leave.guard";
import {techInventoryMissionDetailResolver} from "@tech/pages/inventory/resolvers/tech-inventory-mission-detail.resolver";
import {techInventoryResourceDetailResolver} from "@tech/pages/inventory/resolvers/tech-inventory-resource-detail.resolver";
import {SharedPermissionGuard} from "@shared/guards/shared-permission.guard";
import {AppPermissions} from "@server-models";
import {getSharedIssueRoutes} from "@core/helpers/methods/core-issue-routes.helper";
import {getSharedLoginRoutes} from "@shared/components/login/helper/core-login-routes.helper";
import {techMissionCreatePageCanLeaveGuard} from "@tech/guards/tech-mission-create-page-can-leave.guard";

export default [
  {
    path: 'home',
    pathMatch: 'full',
    redirectTo: '/tech/logged',
  },
  {
    path: 'not-allowed',
    loadComponent: () => import('@shared/components/login/pages/not-allowed/shared-not-allowed.page').then(m => m.SharedNotAllowedPage),
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'tech',
  },
  {
    path: 'tech',
    children: [
      {
        path: '',
        redirectTo: 'login',
        pathMatch: 'full',
      },
      {
        path: 'login',
        loadComponent: () => import('@tech/pages/login/tech-login.page').then(m => m.TechLoginPage)
      },
      {
        path: 'register',
        loadComponent: () => import('@tech/pages/register/tech-register.page').then(m => m.TechRegisterPage)
      },
      ...getSharedLoginRoutes(),
      {
        path: 'logged',
        loadComponent: () => import('@tech/pages/tech-main.page').then(m => m.TechMainPage),
        canActivate: mapToCanActivate([TechAppLoginGuard]),
        children: [
          {
            path: '',
            redirectTo: 'infos',
            pathMatch: 'full',
          },
          {
            path: 'settings',
            loadComponent: () => import('@shared/pages/settings/shared-settings.page').then(m => m.SharedSettingsPage),
          },
          {
            path: 'settings/notifications',
            loadComponent: () => import('@shared/pages/notification-config/shared-notification-settings.page').then(m => m.SharedNotificationSettingsPage),
          },
          {
            path: 'infos',
            loadComponent: () => import('@tech/pages/infos/tech-info.page').then(m => m.TechInfoPage),
            canActivate: mapToCanActivate([SharedPermissionGuard]),
            data: {
              permissions: [AppPermissions.ReadInfo],
              redirectTo: ['tech', 'logged', 'inventory'],
            },
          },
          {
            path: 'inventory',
            loadComponent: () => import('@tech/pages/inventory/tech-inventory.page').then(m => m.TechInventoryPage),
          },
          {
            path: 'inventory/mission/create',
            loadComponent: () => import('@tech/pages/inventory/pages/mission-create/tech-inventory-mission-create-page.component').then(m => m.TechInventoryMissionCreatePage),
            canActivate: mapToCanActivate([SharedPermissionGuard]),
            canDeactivate: [techMissionCreatePageCanLeaveGuard],
            data: {
              permissions: [AppPermissions.CreateMission],
              redirectTo: ['tech', 'logged', 'inventory'],
            },
          },
          {
            path: 'inventory/mission/create/success',
            loadComponent: () => import('@tech/pages/inventory/pages/mission-success/tech-inventory-mission-success-page.component').then(m => m.TechInventoryMissionCreateSuccessPage)
          },
          {
            path: 'inventory/mission/:missionId',
            loadComponent: () => import('@tech/pages/inventory/pages/mission-detail/tech-inventory-mission-detail.page').then(m => m.TechInventoryMissionDetailPage),
            resolve: { missionId: techInventoryMissionDetailResolver },
            children: [
              {
                path: '',
                redirectTo: 'view',
                pathMatch: 'full',
              },
              {
                path: 'view',
                loadComponent: () => import('@tech/pages/inventory/pages/mission-detail/segments/mission-detail/tech-inventory-mission-detail-container.page').then(m => m.TechInventoryMissionDetailContainerPage),
              },
              {
                path: 'report',
                loadComponent: () => import('@tech/pages/inventory/pages/mission-detail/segments/report-detail/tech-inventory-mission-detail-report.page').then(m => m.TechInventoryMissionDetailReportPage),
                canDeactivate: [techReportPageCanLeaveGuard],
              },
              {
                path: 'report/success',
                loadComponent: () => import('@tech/pages/inventory/pages/mission-detail/segments/report-success/tech-inventory-report-success-page.component').then(m => m.TechInventoryReportSuccessPage),
              },
              {
                path: 'chat',
                loadComponent: () => import('@tech/pages/inventory/pages/mission-detail/segments/chat-detail/tech-inventory-mission-detail-chat.page').then(m => m.TechInventoryMissionDetailChatPage),
              },
              {
                path: 'solution',
                loadComponent: () => import('@shared/components/solution/components/solution-list/shared-solution-list.component').then(m => m.SharedSolutionListComponent)
              }
            ],
          },
          {
            path: 'inventory/mission/:missionId/resource/:resourceId',
            loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/tech-inventory-resource-detail.page').then(m => m.TechInventoryResourceDetailPage),
            resolve: { resourceId: techInventoryResourceDetailResolver },
            children: [
              {
                path: '',
                redirectTo: 'view',
                pathMatch: 'full',
              },
              {
                path: 'view',
                loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/segments/resource/tech-inventory-resource-view.page').then(m => m.TechInventoryResourceViewPage),
              },
              {
                path: 'knowledge',
                loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/segments/knowledge/tech-inventory-resource-knowledge.page').then(m => m.TechInventoryResourceKnowledgePage),
              },
              {
                path: 'missions',
                loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/segments/missions/tech-inventory-resource-missions.page').then(m => m.TechInventoryResourceMissionsPage),
              },
              ...getSharedIssueRoutes(),
              {
                path: 'issues',
                loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/segments/issues/tech-inventory-resource-issues.page').then(m => m.TechInventoryResourceIssuesPage),
              },
            ],
          },
          {
            path: 'inventory/resource/:resourceId',
            loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/tech-inventory-resource-detail.page').then(m => m.TechInventoryResourceDetailPage),
            resolve: { resourceId: techInventoryResourceDetailResolver },
            children: [
              {
                path: '',
                redirectTo: 'view',
                pathMatch: 'full',
              },
              {
                path: 'view',
                loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/segments/resource/tech-inventory-resource-view.page').then(m => m.TechInventoryResourceViewPage),
              },
              {
                path: 'knowledge',
                loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/segments/knowledge/tech-inventory-resource-knowledge.page').then(m => m.TechInventoryResourceKnowledgePage),
              },
              {
                path: 'missions',
                loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/segments/missions/tech-inventory-resource-missions.page').then(m => m.TechInventoryResourceMissionsPage),
              },
              ...getSharedIssueRoutes(),
              {
                path: 'issues',
                loadComponent: () => import('@tech/pages/inventory/pages/resource-detail/segments/issues/tech-inventory-resource-issues.page').then(m => m.TechInventoryResourceIssuesPage),
              },
            ],
          },
          {
            path: 'knowledge',
            loadComponent: () => import('@tech/pages/knowledge/tech-knowledge.page').then(m => m.TechKnowledgePage),
            resolve: { data: techMenuResolver },
          },
          {
            path: 'knowledge/article/:articleId',
            loadComponent: () => import('@tech/pages/knowledge/pages/knowledge-article/tech-knowledge-article.page').then(m => m.TechKnowledgeArticlePage),
            resolve: { article: sharedKnowledgeArticleDetailResolver },
          },
          {
            path: 'knowledge/path/:pathId',
            loadComponent: () => import('@tech/pages/knowledge/pages/knowledge-path/tech-knowledge-path.page').then(m => m.TechKnowledgePathPage),
            resolve: { detail: sharedKnowledgePathDetailResolver },
          },
          {
            path: 'knowledge/path/:pathId/exam',
            loadComponent: () => import('@tech/pages/knowledge/pages/knowledge-path/tech-knowledge-path.page').then(m => m.TechKnowledgePathPage),
            resolve: { detail: sharedKnowledgePathDetailResolver },
          },
          {
            path: 'knowledge/path/:pathId/exam/:examId',
            loadComponent: () => import('@tech/pages/knowledge/pages/knowledge-path/tech-knowledge-path.page').then(m => m.TechKnowledgePathPage),
            resolve: { detail: sharedKnowledgePathDetailResolver },
          },
          {
            path: 'documents',
            loadComponent: () => import('@shared/components/documents/shared-document.page').then(m => m.SharedDocumentPage),
            resolve: { data: techMenuResolver },
          },
          {
            path: 'documents/me',
            loadComponent: () => import('@shared/components/documents/pages/personal/shared-document-personal.page').then(m => m.SharedDocumentPersonalPage),
            resolve: { data: techMenuResolver },
          },
          {
            path: 'documents/:path',
            loadComponent: () => import('@shared/components/documents/shared-document.page').then(m => m.SharedDocumentPage),
            resolve: { data: techMenuResolver },
            pathMatch: 'full',
          },
          ...getSharedIssueRoutes(),
          {
            path: 'issues',
            loadComponent: () => import('@tech/pages/issues/tech-issue.page').then(m => m.TechIssuePage),
          }
        ],
      },
    ],
  },
] as Route[];

